import React from "react";
import {
  Heading,
  Flex,
  Button,
  IconButton,
  Stack,
  Image,
  Icon,
  Link,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { RiDeleteBin5Line } from "react-icons/ri";
import { BsCardImage } from "react-icons/bs";
import { PointCard } from "../../fetchers/model";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../utils/Confirmation";
import { useTranslation } from "react-i18next";
import { Table as AITable } from "../../utils/Table";
import axios from "axios";
import { Select } from "chakra-react-select";
import { FilterValue, SortBy } from "../../utils/CommonInterface";
import API from "../../config/api";

export default function ListPointCard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [data, setData] = React.useState<PointCard[]>([]);
  const [pageCount, setPageCount] = React.useState(0);
  const fetchIdRef = React.useRef(0);

  const fetchData = React.useCallback(
    async ({
      pageSize,
      pageIndex,
      globalFilter,
      filters,
      sortBy,
    }: {
      pageSize: number;
      pageIndex: number;
      globalFilter: string;
      filters: FilterValue[];
      sortBy: SortBy[];
    }) => {
      const fetchId = ++fetchIdRef.current;

      setLoading(true);

      const statusFilters = filters.filter(
        (x) => x.id === "status" && x.value.length > 0
      );
      let statusParams =
        (statusFilters.length > 0 &&
          statusFilters[0].value.map((x) => `&status[]=${x}`).join("")) ||
        "";

      let sortByParam =
        (sortBy.length > 0 &&
          `&order=${sortBy[0].desc ? "-" : ""}${sortBy[0].id}`) ||
        "";

      const { data, headers } = await axios.get<PointCard[]>(
        `${API.POINT_CARD}?page=${pageIndex + 1}&per_page=${pageSize}${
          globalFilter ? `&keyword=${globalFilter}` : ""
        }${statusParams}${sortByParam}`
      );
      if (fetchId === fetchIdRef.current) {
        setData(data);
        setPageCount(Math.ceil(parseInt(headers["total"]) / pageSize));

        setLoading(false);
      }
    },
    []
  );

  const statusOptions = ["activate", "deactivate"].map(function (val) {
    return {
      label: t(`point_card.status.${val}`),
      value: val,
    };
  });

  const tableColumns = [
    {
      Header: t("point_card.name"),
      accessor: "name" as const,
      disableFilters: true,
    },
    {
      Header: t("point_card.money_conversion"),
      accessor: "money_conversion" as const,
      disableFilters: true,
      disableSortBy: true,
    },
    {
      Header: t("point_card.point_conversion"),
      accessor: "point_conversion" as const,
      disableFilters: true,
      disableSortBy: true,
    },
    {
      Header: t("point_card.status.title"),
      accessor: "status" as const,
      FilterHeader: t("point_card.status.title"),
      Filter: ({ column }: { column: any }) => (
        <Select
          size="sm"
          isMulti
          name={column.id}
          closeMenuOnSelect={false}
          value={statusOptions.filter(
            (x) => column.filterValue && column.filterValue.includes(x.value)
          )}
          options={statusOptions}
          onChange={(option) => column.setFilter(option.map((x) => x.value))}
        />
      ),
      filterable: true,
    },
    {
      Header: "",
      accessor: "action" as const,
      disableSortBy: true,
      disableFilters: true,
    },
  ];

  const tableData = data.map((item) => ({
    name: (
      <Stack direction="row" spacing={2} alignItems="center">
        {item.logo && item.logo.id ? (
          <Image
            boxSize="36px"
            objectFit="cover"
            src={item.logo.url}
            alt={item.name}
          />
        ) : (
          <Icon as={BsCardImage} boxSize="36px" color={"gray.400"} />
        )}
        <Link
          color="blue.500"
          href={`/console/point_card/${item.id}`}
          fontWeight={"bold"}
        >
          {item.name}
        </Link>
      </Stack>
    ),
    money_conversion: `${item.paid_amount} ¥ = ${item.point_acquired} points`,
    point_conversion: `${item.point_rate} points = ${item.amount_rate} ${
      item.redemption_unit || "¥"
    }`,
    status: (
      <Tag
        size={"sm"}
        borderRadius="full"
        variant="solid"
        colorScheme={item.status === "activate" ? "blue" : "gray"}
      >
        <TagLabel>{t(`point_card.status.${item.status}`)}</TagLabel>
      </Tag>
    ),
    action: (
      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        <IconButton
          icon={<FiEdit2 />}
          aria-label={t("action.edit")}
          variant="link"
          onClick={() => navigate(`/console/point_card/${item.id}/edit`)}
        />
        <ConfirmationDialog
          title={t("point_card.delete.title")}
          description={t("point_card.delete.description", {
            point_card_name: item.name,
          })}
          onConfirm={() => deleteItem(item.id)}
        >
          <IconButton
            icon={<RiDeleteBin5Line />}
            aria-label={t("action.delete")}
            variant="link"
          />
        </ConfirmationDialog>
      </Stack>
    ),
  }));

  const deleteItem = async (itemId: string) => {
    await axios.delete<void>(`${API.POINT_CARD}/${itemId}`);
    setData(data.filter((x) => x.id !== itemId));
  };

  return (
    <>
      <Flex justifyContent="space-between">
        <Heading as="h4" size="lg" mb="4">
          {t("point_card.title.name")}
        </Heading>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          variant="solid"
          onClick={() => navigate("/console/point_card/new")}
        >
          {t("action.new")}
        </Button>
      </Flex>
      <AITable
        columns={tableColumns}
        data={tableData}
        fetchData={fetchData}
        loading={loading}
        pageCount={pageCount}
        useSearch={true}
        disableFilters={false}
        disableSortBy={false}
      />
    </>
  );
}
